/**
 * Protocol Health Dashboard - V2 Tokenomics
 * Two views: Protocol-wide (public) + Per-Hotel (owner)
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Activity, ArrowLeft, ArrowRight, Bitcoin, Building2, CircleDollarSign,
  Coins, Eye, Flame, Gauge, Layers, Lock, RefreshCw,
  Scale, Search, Shield, ShieldAlert, Sparkles,
  TrendingDown, TrendingUp, Users, Zap,
} from 'lucide-react';
import { getProtocolDashboard, getHotelDashboard, getHalvingSchedule } from '@/lib/v2Api';

const fmt = (n) => new Intl.NumberFormat().format(n || 0);

// ─── KPI Card ───
const KpiCard = ({ title, value, subtitle, icon: Icon, trend, testId }) => (
  <Card className="card-hover-lift" data-testid={testId}>
    <CardContent className="pt-5 pb-4 px-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
          <p className="text-2xl md:text-3xl heading-font tabular-nums font-semibold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--web3-primary))]/10 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
        </div>
      </div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          {trend >= 0 ? <TrendingUp className="h-3 w-3 text-green-500" /> : <TrendingDown className="h-3 w-3 text-red-500" />}
          <span className={`text-xs font-medium ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

// ─── Pool Card ───
const PoolCard = ({ name, balance, label, icon: Icon }) => (
  <div className="flex items-center justify-between py-3">
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 rounded-md bg-[hsl(var(--web3-surface-2))]/80 flex items-center justify-center">
        <Icon className="h-4 w-4 text-[hsl(var(--web3-primary))]" />
      </div>
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{name}</p>
      </div>
    </div>
    <p className="text-sm heading-font tabular-nums font-semibold">{fmt(balance)} <span className="text-xs text-muted-foreground">HERMES</span></p>
  </div>
);

// ─── Activity Item ───
const ActivityItem = ({ type, data }) => {
  const configs = {
    mint: { icon: Sparkles, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
    commission: { icon: CircleDollarSign, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    slash: { icon: ShieldAlert, color: 'text-red-500', bg: 'bg-red-500/10' },
  };
  const c = configs[type] || configs.mint;
  const Icon = c.icon;

  const label = type === 'mint'
    ? `Hotel registered: ${fmt(data.mint_amount)} HERMES minted`
    : type === 'commission'
    ? `Booking: ${fmt(data.total_amount)} HERMES (${fmt(data.commission_total)} commission)`
    : `Slash: ${fmt(data.amount)} HERMES (Score: ${data.score_at_slash})`;

  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className={`h-7 w-7 rounded-full ${c.bg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className={`h-3.5 w-3.5 ${c.color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-sm leading-snug">{label}</p>
        <p className="text-xs text-muted-foreground truncate">{data.hotel_id} &middot; {new Date(data.timestamp).toLocaleString()}</p>
      </div>
    </div>
  );
};

// ─── Halving Countdown ───
const HalvingCountdown = ({ halving, schedule }) => {
  const spotsLeft = halving.hotels_until_next_halving;
  const eraPct = halving.hotels_registered > 0
    ? ((halving.hotels_registered % 108) / 108) * 100
    : 0;
  const urgencyColor = spotsLeft <= 10 ? 'text-red-500' : spotsLeft <= 30 ? 'text-amber-500' : 'text-[hsl(var(--web3-primary))]';
  const urgencyGlow = spotsLeft <= 10 ? 'shadow-red-500/20' : spotsLeft <= 30 ? 'shadow-amber-500/15' : 'shadow-[hsl(var(--web3-primary))]/10';

  return (
    <Card className={`relative overflow-hidden border-[hsl(var(--web3-primary))]/20 shadow-lg ${urgencyGlow}`} data-testid="halving-countdown-card">
      <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_85%_20%,rgba(14,165,164,0.08)_0%,transparent_60%)]" />
      <CardContent className="relative pt-6 pb-5 px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1.5fr] gap-6 items-center">
          {/* Countdown Number */}
          <div className="text-center lg:text-left" data-testid="halving-countdown-number">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Spots until halving</p>
            <p className={`text-6xl lg:text-7xl heading-font font-black tabular-nums leading-none ${urgencyColor}`}>
              {spotsLeft}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              of 108 remaining in Era {halving.current_era}
            </p>
            {spotsLeft <= 20 && (
              <div className="mt-2 flex items-center gap-1.5 justify-center lg:justify-start">
                <Flame className="h-3.5 w-3.5 text-amber-500 animate-pulse" />
                <span className="text-xs font-medium text-amber-500">
                  {spotsLeft <= 5 ? 'Almost full! Mint rate drops 50% soon' : 'Filling fast — register now'}
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-20 bg-border" />

          {/* Rate Drop */}
          <div className="text-center" data-testid="halving-rate-drop">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Mint rate at halving</p>
            <div className="flex items-center justify-center gap-3">
              <div className="space-y-0.5">
                <p className="text-2xl heading-font tabular-nums font-bold text-emerald-500">{fmt(halving.current_mint_rate)}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Current</p>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <ArrowRight className="h-5 w-5 text-red-400" />
                <span className="text-[10px] font-semibold text-red-400">-50%</span>
              </div>
              <div className="space-y-0.5">
                <p className="text-2xl heading-font tabular-nums font-bold text-muted-foreground">{fmt(halving.next_mint_rate)}</p>
                <p className="text-[10px] text-muted-foreground uppercase">After</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px h-20 bg-border" />

          {/* Era Progress + Mini Timeline */}
          <div data-testid="halving-era-timeline">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Era {halving.current_era} progress</p>
              <span className="text-xs heading-font tabular-nums font-medium">{Math.round(eraPct)}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-[hsl(var(--web3-surface-2))]/80 overflow-hidden mb-4">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[hsl(var(--web3-primary))] to-emerald-400 transition-all duration-700"
                style={{ width: `${Math.max(eraPct, 2)}%` }}
              />
            </div>
            {/* Mini era dots */}
            {schedule && (
              <div className="flex items-center gap-1">
                {schedule.eras.map((era) => {
                  const isActive = era.era === halving.current_era;
                  const isPast = era.era < halving.current_era;
                  return (
                    <div key={era.era} className="flex-1 group relative">
                      <div className={`h-1.5 rounded-full transition-all ${
                        isActive ? 'bg-[hsl(var(--web3-primary))] shadow-sm shadow-[hsl(var(--web3-primary))]/40' :
                        isPast ? 'bg-emerald-500/60' : 'bg-border'
                      }`} />
                      <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border rounded px-2 py-0.5 text-[10px] whitespace-nowrap z-10 pointer-events-none transition-opacity">
                        Era {era.era}: {fmt(era.mint_per_hotel)}/hotel
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground">Era 1</span>
              <span className="text-[10px] text-muted-foreground">Era 10</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Behavioral Donut ───
const BehavioralChart = ({ verified, failures, misidentified, total }) => {
  const vPct = total > 0 ? (verified / total * 100).toFixed(0) : 0;
  const fPct = total > 0 ? (failures / total * 100).toFixed(0) : 0;
  const mPct = total > 0 ? (misidentified / total * 100).toFixed(0) : 0;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Verified</span>
        <span className="tabular-nums font-medium">{vPct}% ({verified})</span>
      </div>
      <Progress value={Number(vPct)} className="h-2 [&>div]:bg-emerald-500" />
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Failures</span>
        <span className="tabular-nums font-medium">{fPct}% ({failures})</span>
      </div>
      <Progress value={Number(fPct)} className="h-2 [&>div]:bg-red-500" />
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />Misidentified</span>
        <span className="tabular-nums font-medium">{mPct}% ({misidentified})</span>
      </div>
      <Progress value={Number(mPct)} className="h-2 [&>div]:bg-amber-500" />
    </div>
  );
};

// ─── Protocol View ───
const ProtocolView = ({ data, loading, schedule }) => {
  if (loading) return <DashboardSkeleton />;
  if (!data) return null;

  const { halving, btc_treasury, pools, network, behavioral, whistleblower, recent_activity } = data;

  return (
    <div className="space-y-6">
      {/* Halving Countdown - Hero Widget */}
      <HalvingCountdown halving={halving} schedule={schedule} />

      {/* KPI Row */}
      <div className="grid grid-cols-1 gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Hotels on Protocol" value={fmt(network.hotels_registered)} subtitle={`Era ${halving.current_era} of 10`} icon={Building2} testId="kpi-hotels" />
        <KpiCard title="Total Minted" value={`${fmt(halving.total_minted)}`} subtitle={`${fmt(halving.total_remaining_registration_supply)} remaining`} icon={Coins} testId="kpi-minted" />
        <KpiCard title="Total Reputation Stake" value={`${fmt(network.total_staked)}`} subtitle="HERMES across all hotels" icon={Lock} testId="kpi-staked" />
        <KpiCard title="Mint Rate" value={`${fmt(halving.current_mint_rate)}`} subtitle={`${halving.hotels_until_next_halving} hotels until halving`} icon={Zap} testId="kpi-mint-rate" />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Halving Progress */}
        <Card className="lg:col-span-8" data-testid="halving-progress-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Layers className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Halving Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Era {halving.current_era}</p>
                <p className="text-2xl heading-font font-semibold">{halving.hotels_registered} <span className="text-base text-muted-foreground font-normal">/ 1,080 hotels</span></p>
              </div>
              <Badge variant="outline" className="heading-font text-xs">
                Next halving: {fmt(halving.current_mint_rate)} &rarr; {fmt(halving.next_mint_rate)} HERMES
              </Badge>
            </div>
            <Progress value={halving.progress_pct} className="h-3 [&>div]:bg-[hsl(var(--web3-primary))]" data-testid="halving-progress-bar" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Era 1 start</span>
              <span>{halving.progress_pct}% to protocol capacity</span>
              <span>Era 10 (1,080 hotels)</span>
            </div>
          </CardContent>
        </Card>

        {/* BTC Treasury */}
        <Card className="lg:col-span-4" data-testid="btc-treasury-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Bitcoin className="h-5 w-5 text-[hsl(var(--web3-accent))]" />
              BTC Treasury
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center py-2">
              <p className="text-3xl heading-font font-bold">{btc_treasury.amount_btc} BTC</p>
              <Badge variant={btc_treasury.status === 'locked' ? 'default' : 'secondary'} className="mt-2">
                {btc_treasury.status === 'locked' ? 'Locked' : 'Pending Lock'}
              </Badge>
            </div>
            {btc_treasury.lock_end && (
              <p className="text-xs text-center text-muted-foreground">
                Locked until: {new Date(btc_treasury.lock_end).toLocaleDateString()}
              </p>
            )}
            <Separator />
            <p className="text-xs text-muted-foreground text-center">
              3-of-3 MPC consensus required for any movement
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pools + Behavioral + Whistleblower */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Protocol Pools */}
        <Card className="lg:col-span-4" data-testid="protocol-pools-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Protocol Pools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PoolCard name="protocol_staking_reward" label="Protocol Allocation Pool" balance={pools.protocol_staking_reward?.balance} icon={Coins} />
            <Separator />
            <PoolCard name="whistleblower" label="Whistleblower Pool" balance={pools.whistleblower?.balance} icon={Shield} />
            <Separator />
            <PoolCard name="emergency_mutual_fund" label="Emergency Fund" balance={pools.emergency_mutual_fund?.balance} icon={ShieldAlert} />
          </CardContent>
        </Card>

        {/* Behavioral Health */}
        <Card className="lg:col-span-4" data-testid="behavioral-health-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Gauge className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Behavioral Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total tasks</span>
              <span className="heading-font font-semibold">{fmt(behavioral.total_tasks)}</span>
            </div>
            <BehavioralChart
              verified={behavioral.verified_complete}
              failures={behavioral.verified_failure}
              misidentified={behavioral.misidentification}
              total={behavioral.total_tasks}
            />
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rescue tasks</span>
              <span><span className="text-amber-500 font-medium">{behavioral.rescue_pending} pending</span> &middot; {behavioral.rescue_completed} done</span>
            </div>
          </CardContent>
        </Card>

        {/* Whistleblower */}
        <Card className="lg:col-span-4" data-testid="whistleblower-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Scale className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Justice System
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl heading-font font-semibold">{whistleblower.total_claims}</p>
                <p className="text-xs text-muted-foreground">Total Claims</p>
              </div>
              <div>
                <p className="text-2xl heading-font font-semibold text-amber-500">{whistleblower.pending}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div>
                <p className="text-2xl heading-font font-semibold text-emerald-500">{whistleblower.upheld}</p>
                <p className="text-xs text-muted-foreground">Upheld</p>
              </div>
            </div>
            <Separator />
            <div className="text-xs text-muted-foreground space-y-1">
              <p>1.5x asymmetric stake prevents frivolous claims</p>
              <p>Balanced arbitration council from neutral hotels</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Network Totals + Recent Activity */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Card className="lg:col-span-5" data-testid="network-totals-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Users className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Network Totals
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              ['Sage Treasuries', network.total_sage_treasuries],
              ['Bitsy Wallets', network.total_bitsy_wallets],
              ['Booking Revenue', network.total_booking_revenue],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{label}</span>
                <span className="text-sm heading-font tabular-nums font-medium">{fmt(val)} <span className="text-xs text-muted-foreground">HERMES</span></span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-7" data-testid="recent-activity-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Activity className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[240px] pr-3">
              {[
                ...(recent_activity.mints || []).map(d => ({ type: 'mint', data: d, ts: d.timestamp })),
                ...(recent_activity.commissions || []).map(d => ({ type: 'commission', data: d, ts: d.timestamp })),
                ...(recent_activity.slashes || []).map(d => ({ type: 'slash', data: d, ts: d.timestamp })),
              ]
                .sort((a, b) => new Date(b.ts) - new Date(a.ts))
                .slice(0, 10)
                .map((item, i) => (
                  <div key={i}>
                    <ActivityItem type={item.type} data={item.data} />
                    {i < 9 && <Separator />}
                  </div>
                ))}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ─── Hotel View ───
const HotelView = ({ data, loading, hotelId }) => {
  if (loading) return <DashboardSkeleton />;
  if (!data) return (
    <div className="flex flex-col items-center justify-center py-20 text-center space-y-3">
      <Search className="h-12 w-12 text-muted-foreground/40" />
      <p className="text-lg heading-font text-muted-foreground">Enter a Hotel ID above to view its dashboard</p>
    </div>
  );

  const { treasury, staking, marina_escrow, behavioral, hotel_score, commissions, slash_history, staff_scores, claims } = data;
  const tierColors = {
    excellent: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    great: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
    good: 'bg-teal-500/15 text-teal-600 border-teal-500/30',
    warning: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
    poor: 'bg-orange-500/15 text-orange-600 border-orange-500/30',
    unacceptable: 'bg-red-500/15 text-red-600 border-red-500/30',
  };

  return (
    <div className="space-y-6">
      {/* Hotel KPIs */}
      <div className="grid grid-cols-1 gap-4 md:gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard title="Sage Treasury" value={fmt(treasury.sage_treasury_balance)} subtitle="Hotel operations fund" icon={CircleDollarSign} testId="hotel-kpi-sage" />
        <KpiCard title="Bitsy Wallet" value={fmt(treasury.bitsy_wallet_balance)} subtitle="AI agent operational fund" icon={Sparkles} testId="hotel-kpi-bitsy" />
        <KpiCard title="Reputation Stake" value={fmt(staking.staked_amount || 0)} subtitle={`Originally: ${fmt(staking.original_stake || 0)}`} icon={Lock} testId="hotel-kpi-staked" />
        <KpiCard title="Marina Escrow" value={fmt(marina_escrow?.balance || 0)} subtitle={`Total earned: ${fmt(marina_escrow?.total_accumulated || 0)}`} icon={Users} testId="hotel-kpi-escrow" />
      </div>

      {/* Score + Reputation Stake + Behavioral */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Hotel Score */}
        <Card className="lg:col-span-4" data-testid="hotel-score-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Gauge className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Hotel Score
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-2">
              <p className="text-4xl heading-font font-bold">{hotel_score?.final_score || '---'}</p>
              <p className="text-sm text-muted-foreground">/ 1,000</p>
            </div>
            {hotel_score?.components && Object.entries(hotel_score.components).map(([key, val]) => (
              <div key={key} className="flex justify-between text-sm">
                <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="tabular-nums font-medium">{typeof val === 'number' ? val.toFixed(0) : val}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Reputation Stake */}
        <Card className="lg:col-span-4" data-testid="hotel-staking-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Shield className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Reputation Stake Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Tier</span>
              <Badge variant="outline" className={`${tierColors[staking.current_tier] || ''} rounded-full heading-font text-xs`}>
                {(staking.current_tier || 'unknown').toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Score</span>
              <span className="font-medium">{staking.current_score || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Total Slashed</span>
              <span className="font-medium text-red-500">{fmt(staking.total_slashed || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Rewards Earned</span>
              <span className="font-medium text-emerald-500">{fmt(staking.total_rewards_earned || 0)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Rewards</span>
              <Badge variant={staking.rewards_frozen ? 'destructive' : 'default'} className="text-xs">
                {staking.rewards_frozen ? 'Frozen' : 'Active'}
              </Badge>
            </div>
            {slash_history?.length > 0 && (
              <>
                <Separator />
                <p className="text-xs text-muted-foreground font-medium">Recent Slashes</p>
                {slash_history.slice(0, 3).map((s, i) => (
                  <div key={i} className="text-xs flex justify-between">
                    <span className="text-red-500">{fmt(s.amount)} HERMES</span>
                    <span className="text-muted-foreground">{new Date(s.timestamp).toLocaleDateString()}</span>
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        {/* Hotel Behavioral */}
        <Card className="lg:col-span-4" data-testid="hotel-behavioral-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Eye className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Behavioral Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Honesty Rate</span>
              <span className="text-lg heading-font font-semibold">{behavioral.honesty_rate}%</span>
            </div>
            <BehavioralChart
              verified={behavioral.verified_complete}
              failures={behavioral.verified_failure}
              misidentified={behavioral.misidentification}
              total={behavioral.total_tasks}
            />
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rescue pending</span>
              <span className="text-amber-500 font-medium">{behavioral.rescue_pending}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions + Staff + Claims */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <Card className="lg:col-span-6" data-testid="hotel-commissions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Recent Bookings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {(commissions || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No bookings yet</p>
              ) : commissions.map((c, i) => (
                <div key={i} className="flex justify-between items-center py-2.5">
                  <div>
                    <p className="text-sm font-medium">{c.booking_id}</p>
                    <p className="text-xs text-muted-foreground">{new Date(c.timestamp).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm heading-font tabular-nums font-medium">{fmt(c.total_amount)}</p>
                    <p className="text-xs text-muted-foreground">Commission: {fmt(c.commission_total)}</p>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>

        <Card className="lg:col-span-6" data-testid="hotel-staff-scores-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg heading-font flex items-center gap-2">
              <Users className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              Staff Scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              {(staff_scores || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No staff scores yet</p>
              ) : staff_scores.map((s, i) => (
                <div key={i} className="flex justify-between items-center py-2.5">
                  <div>
                    <p className="text-sm font-medium">{s.entity_id}</p>
                    <p className="text-xs text-muted-foreground">{s.tenure_label} &middot; {s.tenure_multiplier}x tenure</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm heading-font tabular-nums font-semibold">{s.final_score?.toFixed(0)}</p>
                    <p className="text-xs text-muted-foreground">{s.review_count} reviews</p>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ─── Loading Skeleton ───
const DashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[1,2,3,4].map(i => (
        <Card key={i}><CardContent className="pt-5 pb-4 space-y-3">
          <Skeleton className="h-3 w-24" /><Skeleton className="h-8 w-32" /><Skeleton className="h-3 w-40" />
        </CardContent></Card>
      ))}
    </div>
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
      <Card className="lg:col-span-8"><CardContent className="pt-6 space-y-4">
        <Skeleton className="h-5 w-48" /><Skeleton className="h-3 w-full" /><Skeleton className="h-8 w-full" />
      </CardContent></Card>
      <Card className="lg:col-span-4"><CardContent className="pt-6 space-y-3">
        <Skeleton className="h-5 w-32" /><Skeleton className="h-12 w-24 mx-auto" /><Skeleton className="h-3 w-full" />
      </CardContent></Card>
    </div>
  </div>
);

// ─── Main Page ───
export default function ProtocolDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('protocol');
  const [protocolData, setProtocolData] = useState(null);
  const [hotelData, setHotelData] = useState(null);
  const [hotelId, setHotelId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);

  const loadProtocol = useCallback(async () => {
    setLoading(true);
    try {
      const [data, schedule] = await Promise.all([
        getProtocolDashboard(),
        getHalvingSchedule(),
      ]);
      setProtocolData(data);
      setScheduleData(schedule);
    } catch (err) {
      toast.error('Failed to load protocol data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHotel = useCallback(async (id) => {
    if (!id) return;
    setHotelLoading(true);
    try {
      const data = await getHotelDashboard(id);
      setHotelData(data);
      setHotelId(id);
    } catch (err) {
      toast.error(`Hotel "${id}" not found`);
      setHotelData(null);
    } finally {
      setHotelLoading(false);
    }
  }, []);

  useEffect(() => { loadProtocol(); }, [loadProtocol]);

  const handleHotelSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      loadHotel(searchInput.trim());
    }
  };

  return (
    <div className="admin-theme min-h-dvh bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl heading-font font-semibold tracking-tight" data-testid="dashboard-title">Protocol Dashboard</h1>
              <p className="text-xs text-muted-foreground">HERMES Protocol V2 Tokenomics</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadProtocol(); if (hotelId) loadHotel(hotelId); }} data-testid="refresh-button">
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button size="sm" onClick={() => navigate('/register-hotel')} data-testid="register-hotel-button">
            <Building2 className="h-4 w-4 mr-2" />Register Hotel
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
            <TabsList data-testid="dashboard-tabs">
              <TabsTrigger value="protocol" data-testid="tab-protocol">
                <Activity className="h-4 w-4 mr-2" />Protocol
              </TabsTrigger>
              <TabsTrigger value="hotel" data-testid="tab-hotel">
                <Building2 className="h-4 w-4 mr-2" />Hotel
              </TabsTrigger>
            </TabsList>

            {activeTab === 'hotel' && (
              <form onSubmit={handleHotelSearch} className="flex gap-2 flex-1 max-w-md">
                <Input
                  placeholder="Enter Hotel ID (e.g. grand-marina-001)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="text-sm"
                  data-testid="hotel-id-input"
                />
                <Button type="submit" size="sm" variant="outline" data-testid="hotel-search-button">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            )}
          </div>

          <TabsContent value="protocol">
            <ProtocolView data={protocolData} loading={loading} schedule={scheduleData} />
          </TabsContent>

          <TabsContent value="hotel">
            <HotelView data={hotelData} loading={hotelLoading} hotelId={hotelId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
